import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { CircularProgress, Tooltip, IconButton } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { format } from 'date-fns';
import classnames from 'classnames';
import { AgGrid } from '../../components/AgGrid';
import { getAutomationRunById } from '../../services';
import ErrorMessage from '../../components/Error/ErrorMessage';
import { useParams } from 'react-router-dom';

const LogLevelCellRenderer = (props) => {
  const value = props.value?.toLowerCase();
  
  if (!value) {
    return <span>-</span>;
  }
  
  const iconStyles = {
    error: { icon: ErrorIcon, color: '#f44336', title: 'Error' },
    warning: { icon: WarningIcon, color: '#ff9800', title: 'Warning' },
    info: { icon: InfoIcon, color: '#2196f3', title: 'Info' }
  };

  const config = iconStyles[value] || iconStyles.info;
  const IconComponent = config.icon;

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Tooltip title={config.title} placement="top">
        <IconComponent style={{ color: config.color, fontSize: 24 }} />
      </Tooltip>
    </div>
  );
};

const LogDateCellRenderer = (props) => {
  const value = props.value;
  
  if (!value) {
    return <span>-</span>;
  }

  try {
    const date = new Date(value);
    const formattedDate = format(date, 'MM/dd/yyyy hh:mm:ss a');
    
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span>{formattedDate}</span>
      </div>
    );
  } catch (error) {
    console.error('Error parsing date:', error);
    return <span>{value}</span>;
  }
};

const styles = theme => ({});

export const AutomationLogsGrid = withStyles(styles)(props => {
  const [automationRun, setAutomationRun] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const { automationRunId } = useParams();

  const fetchAutomationRunById = async () => {
    try {
      const automationRunData = await getAutomationRunById(automationRunId);
      setAutomationRun(automationRunData);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomationRunById();
  }, []);

  const columnDefs = [
    { headerName: 'Log ID', field: 'automationLogId', hide: true },
    { headerName: 'Run ID', field: 'automationRunId', hide: true },
    { 
      headerName: 'Log Date', 
      field: 'logDate', 
      width: 200,
      type: 'dateColumn',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const cellDate = new Date(cellValue);
          if (cellDate < filterLocalDateAtMidnight) {
            return -1;
          } else if (cellDate > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        }
      },
      cellRenderer: 'logDateCellRenderer',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      sort: 'desc',
      valueGetter: (params) => {
        return params.data?.logDate ? new Date(params.data.logDate) : null;
      }
    },
    { 
      headerName: 'Log Level', 
      field: 'logLevel', 
      width: 125,
      cellRenderer: 'logLevelCellRenderer',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['Error', 'Warning', 'Info'],
        cellRenderer: 'logLevelCellRenderer'
      }
    },
    { 
      headerName: 'Log Message', 
      field: 'message', 
      width: 500,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      filter: 'agTextColumnFilter'
    }
  ];

  const gridOptions = {
    suppressColumnVirtualisation: true,
    defaultColDef: {
      resizable: true,
      suppressAutoSize: false,
      flex: 1
    },
    frameworkComponents: {
      logLevelCellRenderer: LogLevelCellRenderer,
      logDateCellRenderer: LogDateCellRenderer
    },
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [10, 25, 50, 100],
    suppressPaginationPanel: false
  };

  if (isLoading) {
    return (
      <div className={classnames('flex-1', 'card', 'border-left-green')} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }
  if (isError) {
    return <ErrorMessage message="There was an error fetching automations." />;
  }

  return (
    <div className={classnames('flex-1', 'card', 'border-left-green')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <h3 style={{ margin: 0 }}>{automationRun.friendlyJobName} - Run {automationRun.identifier} Logs</h3>
        <Tooltip title="Log history is limited to the most recent 200 entries" placement="right">
          <IconButton size="small" style={{ padding: 0 }}>
            <HelpOutlineIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </div>
      <AgGrid
        gridData={automationRun.automationLogs}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        redirectOnClick={true}
        editOnClick={false}
        handleCellClick={evt => {
          console.log('Clicked row data:', evt.data);
          const automationLogId = evt.data.automationLogId;
          if (automationLogId) {
            window.location = `${window.location.href}/${automationLogId}`;
          }
        }}
        rowClassRules={{}}
      />
    </div>
  );
});
